import { resolveGalleryImageUrl } from "@/utils/helpers";
import {
  mvpApiGetJobTask,
  type JobTaskDetailSchema,
  type OoSParcelSchema,
} from "@augmodo/lebron-browser-client";
import { mvpApiGetOosParcelOptions } from "@labeling-tools/api/lebron/generated/@tanstack/react-query.gen";
import {
  imageGalleryQueryKey,
  imageGalleryQueryOptions,
  useFetchJobTasks,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  taskParcelsQueryOptions,
  useRelatedFramesEnabled,
} from "./useTaskRelatedFrames";
import { selectNextPrefetchTaskId } from "./useNextTaskPrefetch";

const STALE_TIME = 30 * 1000;

type Args = {
  jobId: string | undefined;
  currentTaskId: string | undefined;
  /** A benchmark task's parcel labels are its gold answer — never fetch them. */
  isCurrentTaskBenchmark?: boolean;
  enabled: boolean;
};

/**
 * Warms the react-query cache and the browser HTTP cache with the *next* pending
 * CoverageBeta task's data, so submitting the current one transitions with no
 * loading state. Mirrors `useNextTaskPrefetch` (the confirmation flow) but warms
 * the keys CoverageBeta actually reads: the parcel via `mvpApiGetOosParcelOptions`
 * and every image (query + all references) via the per-id gallery cache that
 * `useFetchGalleryImgUrl` consumes.
 *
 * Returns the prefetched task detail plus an `isPrefetchReady` flag; the submit
 * handler flips it into the next-pending-task cache when ready (see
 * CoverageBetaSubmitBtn).
 */
export function useCoverageBetaNextTaskPrefetch({
  jobId,
  currentTaskId,
  isCurrentTaskBenchmark = false,
  enabled,
}: Args) {
  const queryClient = useQueryClient();

  const { data: jobTasks } = useFetchJobTasks(
    enabled && jobId ? jobId : undefined,
  );

  // The current task's frames (the same cached query its scrubber reads) say
  // which other tasks its labels will be propagated onto on submit — those go
  // last, matching lebron's hold, so they open pre-filled. Never fetched for
  // a benchmark task, whose parcel's labels are its gold answer.
  const relatedFramesEnabled = useRelatedFramesEnabled();
  const { data: currentParcels } = useQuery({
    ...taskParcelsQueryOptions(jobId ?? "", currentTaskId ?? ""),
    enabled:
      !!enabled &&
      relatedFramesEnabled &&
      !isCurrentTaskBenchmark &&
      !!jobId &&
      !!currentTaskId,
  });
  const relatedTaskIds = useMemo(
    () =>
      new Set(
        (currentParcels ?? []).flatMap((p) => (p.task_id ? [p.task_id] : [])),
      ),
    [currentParcels],
  );

  const nextTaskId: string | undefined = useMemo(() => {
    if (!enabled) return undefined;
    return selectNextPrefetchTaskId(
      jobTasks?.tasks,
      currentTaskId,
      relatedTaskIds,
    );
  }, [enabled, jobTasks, currentTaskId, relatedTaskIds]);

  // Next task detail — the object the submit handler flips into the
  // next-pending-task cache.
  const { data: nextTask } = useQuery<JobTaskDetailSchema>({
    queryKey: ["fetch-task", jobId, nextTaskId],
    queryFn: async () => {
      const r = await mvpApiGetJobTask({
        path: { job_id: jobId as string, task_id: nextTaskId as string },
      });
      if (r.error) throw new Error(String(r.error));
      return r.data as JobTaskDetailSchema;
    },
    enabled: !!enabled && !!jobId && !!nextTaskId,
    staleTime: STALE_TIME,
  });

  // The next task's related-parcel list (signed URLs only — the frames
  // themselves preload once it mounts, so they don't compete with its query
  // and reference images). Never for a benchmark task, whose parcel's labels
  // are its gold answer.
  useQuery({
    ...taskParcelsQueryOptions(jobId ?? "", nextTaskId ?? ""),
    enabled:
      !!enabled &&
      relatedFramesEnabled &&
      !!jobId &&
      !!nextTask &&
      (nextTask as any).type !== "COVERAGE_REVIEW_BENCHMARK",
  });

  const nextParcelId = (nextTask?.data as { parcel_id?: string } | undefined)
    ?.parcel_id;

  // Next parcel — SAME key/options CoverageBetaTask reads, so the flip finds it
  // warm and renders without a parcel fetch.
  const { data: nextParcel } = useQuery<OoSParcelSchema>({
    ...mvpApiGetOosParcelOptions({ path: { parcel_id: nextParcelId ?? "" } }),
    enabled: !!enabled && !!nextParcelId,
  });

  // Every image the next task shows: the query image + all reference images.
  const imageIds = useMemo(() => {
    const queryImageId = (
      nextParcel?.query as { image_id?: string } | undefined
    )?.image_id;
    const refIds = ((nextParcel?.references ?? []) as { image_id?: string }[])
      .map((r) => r.image_id)
      .filter((id): id is string => !!id);
    return [...(queryImageId ? [queryImageId] : []), ...refIds];
  }, [nextParcel]);

  // Fetch all of them in one gallery request (rather than N), then seed each
  // per-id gallery cache so `useFetchGalleryImgUrl([id])` hits it instantly.
  const { data: gallery } = useQuery({
    ...imageGalleryQueryOptions(imageIds.length ? imageIds : undefined),
    enabled: !!enabled && imageIds.length > 0,
  });

  const [imagesReady, setImagesReady] = useState(false);
  useEffect(() => {
    if (!enabled || !gallery?.images?.length) {
      setImagesReady(false);
      return;
    }
    // Seed the per-id caches the components read.
    for (const img of gallery.images) {
      queryClient.setQueryData(imageGalleryQueryKey([img.id]), {
        images: [img],
      });
    }
    // Pre-decode the optimized bytes into the browser HTTP cache so the <img>
    // renders instantly once the next task mounts.
    const urls = gallery.images
      .map((img) => resolveGalleryImageUrl(img))
      .filter((u): u is string => !!u);
    if (!urls.length) {
      setImagesReady(true);
      return;
    }
    let cancelled = false;
    let loaded = 0;
    let failed = 0;
    const settle = () => {
      if (!cancelled && loaded + failed === urls.length) setImagesReady(true);
    };
    urls.forEach((url) => {
      const im = new Image();
      im.onload = () => {
        loaded += 1;
        settle();
      };
      im.onerror = () => {
        failed += 1;
        settle();
      };
      im.src = url;
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, gallery, queryClient]);

  const isPrefetchReady = !!(
    enabled &&
    nextTaskId &&
    nextTask &&
    nextParcel &&
    (imageIds.length === 0 || imagesReady)
  );

  return { nextTaskId, nextTaskDetail: nextTask, isPrefetchReady };
}
